import React, { useEffect, useRef, useState } from 'react'
import profileImg from '../../assets/globalImgs/profile.png'
import axios from 'axios';
import moment from 'moment';
import { Link, useNavigate } from 'react-router-dom';
import useHandleErrors from '../../utils/user.utils';
import SignatureCanvas from 'react-signature-canvas';
import { toast, ToastContainer } from 'react-toastify';
import { useUser } from '../../utils/userContext';
import Select from "react-select";

const PersonalInformation = () => {
    const [formData, setFormData] = useState({
        personal: {
            firstName: '',
            lastName: '',
            email: '',
            phone: '',
            dateOfBirth: '',
            gender: '',
        },
        address: {
            present: {
                addressLine1: '',
                addressLine2: '',
                townCity: '',
                state: '',
                postCode: '',
                countryCode: '',
            },
            permanent: {
                addressLine1: '',
                addressLine2: '',
                townCity: '',
                state: '',
                postCode: '',
                countryCode: '',
            },
        },
        education: {
            institutionName: '',
            institutionLocation: {
                city: '',
                region: '',
            },
            institutionType: '',
            qualification: '',
            graduationDate: '',
            gradeCGPA: '',
        },
        experience: {
            department: '',
            designation: '',
            employerName: '',
            endDate: '',
            locationCity: '',
            managerEmail: '',
            managerName: '',
            startDate: '',
            totalExperience: ''
        },
        achievements: [
            { Title: '', AchievementDate: '', Comments: '' }
        ],
        skills: [],
        health: {
            medicalConditionsAllergies: '',
            chronicConditions: '',
            chronicAllergies: '',
            vaccinationStatus: '',
            vaccinationType: '',
            lastVaccinationDate: '',
            workRelatedInjuries: '',
            incidentDate: '',
            incidentDescription: '',
            hasPersonalDetails: '',
            doctorName: '',
            doctorContact: '',
            doctorEmail: '',
            hospitalName: '',
            healthCertificate: null,
        },
        additional: {
            kin: '',
            passport: '',
            passportCopy: null,
            conductCertificate: null,
            referees: '',
            digitalSignature: ''
        },
        submittedDocuments: {
            documentUpload: null,
            uploadedDocuments: [],
        },
        familyDetails: {
            fatherName: '',
            motherName: '',
            birthCertificate: null,
            spouseName: '',
            marriageCertificate: null,
            children: "", // Default to "No"
            childrenDetails: [],
        },
        bankDetails: {
            bankName: '',
            branchName: '',
            branchCode: '',
            accountNumber: '',
            accountType: '',
            swiftCode: '',
        },
    });
    const [userDetails, setUserDetails] = useState([]);
    const token = localStorage.getItem("token");
    const handleErrors = useHandleErrors();
    const [showSignaturePad, setShowSignaturePad] = useState(false);
    const sigCanvas = useRef({});
    const { user } = useUser();
    const [signatureURL, setSignatureURL] = useState('');
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const hasFetchedRef = useRef(false);
    const [uploadedFiles, setUploadedFiles] = useState([]);
    const [file, setFile] = useState(null);

    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    const handleSaveSignature = () => {
        const canvas = sigCanvas.current.getTrimmedCanvas();
        const ctx = canvas.getContext('2d');

        // Create a new canvas to avoid modifying the original canvas directly
        const newCanvas = document.createElement('canvas');
        newCanvas.width = canvas.width;
        newCanvas.height = canvas.height;
        const newCtx = newCanvas.getContext('2d');

        // Fill the background with white
        newCtx.fillStyle = 'white';
        newCtx.fillRect(0, 0, newCanvas.width, newCanvas.height);

        // Draw the original signature on top of the white background
        newCtx.drawImage(canvas, 0, 0);

        // Save the canvas as a PNG
        const signature = newCanvas.toDataURL('image/png');
        toast.success("Signature Saved Successfully.", {
            position: toast.POSITION.TOP_CENTER,
        });
        setSignatureURL(signature);
        setShowSignaturePad(false);
    };


    const handleRetrySignature = () => {
        sigCanvas.current.clear();
        setSignatureURL('');
    };

    const handleSubmit = () => {
        if (!signatureURL) {
            toast.error("Please Enter Signature.", {
                position: toast.POSITION.TOP_CENTER,
            });
            return
        }
        if (formData?.health?.doctorEmail && !emailPattern.test(formData?.health?.doctorEmail)) {
            toast.error("Please enter a valid email address.", {
                position: toast.POSITION.TOP_CENTER,
            });
            return
        }
        if (formData?.experience?.managerEmail && !emailPattern.test(formData?.experience?.managerEmail)) {
            toast.error("Please enter a valid email address.", {
                position: toast.POSITION.TOP_CENTER,
            });
            return
        }
        setIsModalOpen(true);
    };

    useEffect(() => {
        if (!hasFetchedRef.current) {
            fetchEmployeeDetails();
            hasFetchedRef.current = true;
        }
    }, []);

    const fetchEmployeeDetails = async () => {
        setLoading(true);
        try {
            const path = `/getPersonalDetails?employeeId=${user?.EmployeeId}`;
            const URL = process.env.REACT_APP_BE_URL + path;
            const response = await axios.get(URL, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            }); // Replace with your actual API endpoint
            if (response.status === 200) {
                setUserDetails(response.data);
                const { personalInformation } = response.data;

                // Check if personalInformation and personalDetails have any meaningful data
                if (personalInformation && Object.keys(personalInformation).length > 0) {
                    const { personalDetails } = personalInformation;

                    // Function to check if a field is filled
                    const isFieldFilled = (value) => {
                        if (Array.isArray(value)) {
                            return value.length > 0; // For arrays, check if it has elements
                        }
                        if (typeof value === 'object' && value !== null) {
                            return Object.values(value).some(isFieldFilled); // Recursively check nested objects
                        }
                        return !!value; // For strings, numbers, etc., check if truthy
                    };

                    // Function to calculate the percentage of filled fields
                    const calculateFilledPercentage = (data) => {
                        const totalFields = Object.values(data).reduce((count, value) => {
                            if (typeof value === 'object' && value !== null) {
                                // If the value is an object, count its fields recursively
                                return count + Object.keys(value).length;
                            }
                            return count + 1; // Count each non-object field
                        }, 0);

                        const filledFields = Object.values(data).reduce((count, value) => {
                            if (typeof value === 'object' && value !== null) {
                                // If the value is an object, count filled fields recursively
                                return count + Object.values(value).filter(isFieldFilled).length;
                            }
                            return count + (isFieldFilled(value) ? 1 : 0); // Count filled non-object fields
                        }, 0);

                        return (filledFields / totalFields) * 100; // Calculate percentage
                    };

                    // Check if personalDetails is filled up to 80%
                    const filledPercentage = calculateFilledPercentage(personalDetails);
                    console.log("filledPercentage", filledPercentage)

                    if (filledPercentage >= 80) {
                        navigate(`/EmployeeDetails/${user?.EmployeeId}`);
                    } else {
                        console.log("Please fill at least 80% of the fields to proceed.");
                    }

                }
            }
            else {
                handleErrors(response)
            }
        } catch (error) {
            console.error('Error fetching employee details:', error);
            if (error.response) {
                handleErrors(error.response);
            } else {
                toast.error("Something went wrong. Please try again later.", {
                    position: toast.POSITION.TOP_CENTER,
                });
            }
        } finally {
            setLoading(false);
        }
    };

    const handleFileUpload = (event) => {
        const fieldName = event.target.id;
        const file = event.target.files[0];
        setFormData({
            ...formData,
            health: {
                ...formData.health,
                [fieldName]: file,
            },
            additional: {
                ...formData.additional,
                [fieldName]: file,
            },
            submittedDocuments: {
                ...formData.submittedDocuments,
                documentUpload: file,
            },
            familyDetails: {
                ...formData.familyDetails,
                [fieldName]: file,
            },
        });
    };

    const handleDeleteDocument = (index) => {
        const updatedDocuments = [...formData.submittedDocuments.uploadedDocuments];
        updatedDocuments.splice(index, 1);
        setFormData({
            ...formData,
            submittedDocuments: {
                ...formData.submittedDocuments,
                uploadedDocuments: updatedDocuments,
            },
        });
    };

    const handleYes = async (event) => {
        event.preventDefault();
        setLoading(true);
        setIsModalOpen(false);
        // Prepare multi-part form data for files (images/documents)
        const multiFormData = new FormData();
        if (formData.health.healthCertificate) {
            multiFormData.append('healthCertificate', formData.health.healthCertificate);
        }
        if (formData.additional.passportCopy) {
            multiFormData.append('PassportCopy', formData.additional.passportCopy);
        }
        if (formData.additional.conductCertificate) {
            multiFormData.append('ConductCertificates', formData.additional.conductCertificate);
        }
        if (formData.familyDetails.birthCertificate) {
            multiFormData.append('BirthCertificate', formData.familyDetails.birthCertificate);
        }
        if (formData.familyDetails.marriageCertificate) {
            multiFormData.append('MarriageCertificate', formData.familyDetails.marriageCertificate);
        }

        // Append achievements certificates to multiFormData
        formData.achievements.forEach((achievement, index) => {
            if (achievement.Certificate) {
                multiFormData.append(`SkillDocuments`, achievement.Certificate);
            }
        });
        uploadedFiles.forEach((file, index) => {
            multiFormData.append(`submittedDocuments`, file);
        });


        // Prepare JSON payload for other form fields
        const jsonPayload = JSON.stringify({
            EmployeeId: user?.EmployeeId,
            // user?.EmployeeId,
            ActiveFlag: "Y",
            // personal: formData.personal,
            // address: formData.address,
            PersonalDetails: {
                Bank: {
                    BankName: formData.bankDetails.bankName,
                    BranchName: formData.bankDetails.branchName,
                    BranchCode: formData.bankDetails.branchCode,
                    AccountNumber: formData.bankDetails.accountNumber,
                    AccountType: formData.bankDetails.accountType,
                    SwiftCode: formData.bankDetails.swiftCode,
                },
                Family: {
                    FatherName: formData.familyDetails.fatherName,
                    MotherName: formData.familyDetails.motherName,
                    SpouseName: formData.familyDetails.spouseName,
                    Children: formData.familyDetails.children,
                    childrenDetails: formData.familyDetails.childrenDetails,
                },
                Education: {
                    InstitutionName: formData.education.institutionName,
                    InstitutionCode: formData.education.institutionLocation.city,
                    //  {
                    //   city: formData.education.institutionLocation.city,
                    //   region: formData.education.institutionLocation.region,
                    // },
                    InstitutionType: formData.education.institutionType,
                    Qualification: formData.education.qualification,
                    GraduationDate: formData.education.graduationDate,
                    Grade: formData.education.gradeCGPA,
                },
                Experience: {
                    Department: formData.experience.department,
                    Designation: formData.experience.designation,
                    EmployerName: formData.experience.employerName,
                    EndDate: formData.experience.endDate,
                    Location: formData.experience.locationCity,
                    ManagerEmail: formData.experience.managerEmail,
                    ManagerName: formData.experience.managerName,
                    StartDate: formData.experience.startDate,
                    TotalExperience: formData.experience.totalExperience
                },
                // Inside the JSON payload
                Achievements: formData.achievements.map(({ Title, AchievementDate, Comments }) => ({
                    Title,
                    AchievementDate,
                    Comments
                })),
                Skills: formData.skills,

                Health: {
                    MedicalConditions: formData.health.medicalConditionsAllergies,
                    KnownChronicConditions: formData.health.chronicConditions,
                    KnownChronicAllergies: formData.health.chronicAllergies,
                    VaccinationStatus: formData.health.vaccinationStatus,
                    VaccinationType: formData.health.vaccinationType,
                    DateOfLastVaccination: formData.health.lastVaccinationDate,
                    WorkRelatedInjuries: formData.health.workRelatedInjuries,
                    DateOfIncident: formData.health.incidentDate,
                    IncidentDescription: formData.health.incidentDescription,
                    DoYouHavePersonalDoctor: formData.health.hasPersonalDetails,
                    DoctorName: formData.health.doctorName,
                    DoctorContact: formData.health.doctorContact,
                    DoctorEmail: formData.health.doctorEmail,
                    HospitalName: formData.health.hospitalName,
                },
                AdditionalInformation: {
                    Kin: formData.additional.kin,
                    Passport: formData.additional.passport,
                    ReferenceDetails: formData.additional.referees,
                    DigitalSignature: signatureURL
                },
            }
        });

        multiFormData.append('jsonPayload', jsonPayload)

        try {
            const path = "/personalDetails";
            const URL = process.env.REACT_APP_BE_URL + path;
            const response = await axios.post(URL, multiFormData, {
                method: 'POST',
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            })
            if (response.status === 200) {
                navigate(`/EmployeeDetails/${user?.EmployeeId}`);
                sigCanvas.current.clear();
                setSignatureURL('');
            } else {
                handleErrors(response);
            }
        } catch (error) {
            if (error.response) {
                handleErrors(error.response);
            } else {
                toast.error("Something went wrong. Please try again later.", {
                    position: toast.POSITION.TOP_CENTER,
                });
            }
        } finally {
            setLoading(false);
        }
    };

    const [hasChildren, setHasChildren] = useState(
        userDetails?.personalInformation?.personalDetails?.family?.children
            ? "Yes"
            : "No"
    );
    const [childrenDetails, setChildrenDetails] = useState(
        userDetails?.personalInformation?.personalDetails?.family
            ?.childrenDetails || []
    );

    // Handle dropdown change for children status
    const handleDropdownChange = (selectedOption) => {
        const value = selectedOption ? selectedOption.value : '';
        setHasChildren(value);

        if (value === "No") {
            setChildrenDetails([]);
        } else if (value === "Yes" && childrenDetails.length === 0) {
            setChildrenDetails([{ ChildrenName: "", Age: "" }]);
        }
    };

    // Handle changes to children details
    const handleChildrenChange = (index, e) => {
        const { name, value } = e.target;
        setChildrenDetails((prev) => {
            const updatedDetails = [...prev];
            updatedDetails[index] = { ...updatedDetails[index], [name]: value };
            return updatedDetails;
        });
    };

    // Add a new child entry
    const addChild = () => {
        setChildrenDetails((prev) => [...prev, { ChildrenName: "", Age: "" }]);
    };

    // Remove a child entry
    const removeChild = (index) => {
        setChildrenDetails((prev) => prev.filter((_, i) => i !== index));
    };

    const options = [
        { value: 'Yes', label: 'Yes' },
        { value: 'No', label: 'No' },
    ];
    const handleMedicalChange = (selectedOption) => {
        setFormData((prev) => ({
            ...prev,
            health: {
                ...prev.health,
                medicalConditionsAllergies: selectedOption.value,
            },
        }));
    };
    // Options for Vaccination Status dropdown
    const vaccinationOptions = [
        { value: 'Yes', label: 'Yes' },
        { value: 'No', label: 'No' },
    ];

    // Handler for Vaccination Status change
    const handleVaccinationChange = (selectedOption) => {
        setFormData((prev) => ({
            ...prev,
            health: {
                ...prev.health,
                vaccinationStatus: selectedOption.value,
            },
        }));
    };
    // Options for Work-Related Injuries and Illnesses dropdown
    const workInjuryOptions = [
        { value: 'Yes', label: 'Yes' },
        { value: 'No', label: 'No' },
    ];

    // Handler for Work-Related Injuries and Illnesses change
    const handleWorkInjuryChange = (selectedOption) => {
        setFormData((prev) => ({
            ...prev,
            health: {
                ...prev.health,
                workRelatedInjuries: selectedOption.value,
            },
        }));
    };
    // Options for the dropdown
    const personalDetailsOptions = [
        { value: 'Yes', label: 'Yes' },
        { value: 'No', label: 'No' },
    ];

    // Handler for changes in the dropdown
    const handlePersonalDetailsChange = (selectedOption) => {
        setFormData((prev) => ({
            ...prev,
            health: {
                ...prev.health,
                hasPersonalDetails: selectedOption.value,
            },
        }));
    };
    const handleKeyPress = (event) => {
        const charCode = event.charCode || event.keyCode;
        if (charCode < 48 || charCode > 57) {
            event.preventDefault();
        }
    };
    return (
        <div>
            {loading && (
                <div id="preloading">
                    <span className="loader"></span>
                </div>
            )}
            <ToastContainer />
            <div class="content-page">
                <div class="container-fluid">
                    <div class="row mb-2">
                        <div class="col">
                            <h3 class="title mb-2">Personal Information</h3>
                        </div>
                        <div class="col-auto">
                            <Link to={'/HrConnect'} data-extra-toggle="tab" data-target="#organisation-pan" class="btn btn-primary mt-lg-0 mt-4"><i class="ri-user-2-fill"></i> HR Connect</Link>
                        </div>
                    </div>

                    <div class="row">
                        <div class="col-lg-4">
                            <div class="card mb-4">
                                <div className="card-body">
                                    <div className="user-info">
                                        <div className="profile-detail">
                                            <div className="user-profile-pic">
                                                <img src={userDetails?.onboardEmployeeDetails?.Image || profileImg} className="img-fluid" alt="Profile" />
                                            </div>
                                        </div>
                                        <div className="text-center mt-3 mb-4">
                                            <h6>{`${userDetails?.onboardEmployeeDetails?.FirstName} ${userDetails?.onboardEmployeeDetails?.MiddleName} ${userDetails?.onboardEmployeeDetails?.LastName}`}</h6>
                                        </div>
                                        <div className="row mt-3">
                                            <div className="form-group col-md-6 col-lg-12 col-sm-6">
                                                <small className="form-text text-muted">Official Email</small>
                                                <input
                                                    type="email"
                                                    className="form-control-plaintext"
                                                    value={userDetails?.onboardEmployeeDetails?.OfficialEmail}
                                                    readOnly
                                                />
                                            </div>
                                            <div className="form-group col-md-6 col-lg-6 col-sm-6">
                                                <small className="form-text text-muted">User Access</small>
                                                <input
                                                    type="text"
                                                    className="form-control-plaintext"
                                                    value={userDetails?.onboardEmployeeDetails?.UserAccessLevel}
                                                    readOnly
                                                />
                                            </div>
                                            <div className="form-group col-md-6 col-lg-6 col-sm-6">
                                                <small className="form-text text-muted">Employment Type</small>
                                                <input
                                                    type="text"
                                                    className="form-control-plaintext"
                                                    value={userDetails?.onboardEmployeeDetails?.EmployeeType}
                                                    readOnly
                                                />
                                            </div>
                                            <div className="form-group col-md-6 col-lg-12 col-sm-6">
                                                <small className="form-text text-muted">Manager Email</small>
                                                <input
                                                    type="email"
                                                    className="form-control-plaintext"
                                                    value={userDetails?.onboardEmployeeDetails?.ManagerEmail}
                                                    readOnly
                                                />
                                            </div>
                                            <div className="form-group col-md-12">
                                                <label htmlFor="mobno">Contact Number</label>
                                                <div className="input-group">
                                                    <input
                                                        type="text"
                                                        className="form-control"
                                                        value={userDetails?.onboardEmployeeDetails?.ContactNumber}
                                                        readOnly
                                                    />
                                                </div>
                                            </div>
                                            <div className="form-group col-md-12 collapse show" id="altContactNumber">
                                                <label htmlFor="altconno">Alternate Contact</label>
                                                <div className="input-group">
                                                    <input
                                                        type="text"
                                                        className="form-control"
                                                        value={userDetails?.onboardEmployeeDetails?.AlternateContactNumber}
                                                        readOnly
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div class="col-lg-8">
                            <div class="card mb-4">
                                <div class="card-body" id="ORGEmployeeDetails">
                                    <h5 class="title accordion-title collapsed" data-toggle="collapse" data-target="#basicDetails"><i class="ri-information-line" title="Raise request to HR for update"></i> Basic</h5>
                                    <div class="row collapse" id="basicDetails" data-parent="#ORGEmployeeDetails">
                                        <div class="form-group col-md-6">
                                            <small class="form-text text-muted">User ID</small>
                                            <input type="text" class="form-control-plaintext" value={userDetails?.onboardEmployeeDetails?.UserId} readonly />
                                        </div>
                                        <div className="form-group col-md-6">
                                            <label>Digital Signature</label>
                                            {showSignaturePad ? (
                                                <div>
                                                    <SignatureCanvas
                                                        penColor="black"
                                                        ref={sigCanvas}
                                                        canvasProps={{ className: 'sigCanvas' }}
                                                    />
                                                    <div className="text-center mt-2">
                                                        <button onClick={handleRetrySignature} className="btn btn-light">
                                                            <i className="ri-loop-right-line"></i> Retry
                                                        </button>
                                                        <button onClick={handleSaveSignature} className="btn btn-primary">
                                                            Save <i className="ri-checkbox-circle-line"></i>
                                                        </button>
                                                    </div>
                                                    {signatureURL && (
                                                        <a href={signatureURL} className="d-block mt-2" download="digital-signature.png">
                                                            Download Digital Signature
                                                        </a>
                                                    )}
                                                </div>
                                            ) : (
                                                <div className="input-group">
                                                    <div className="input-group-append">
                                                        <button
                                                            type="button"
                                                            className="btn btn-secondary"
                                                            onClick={() => setShowSignaturePad(true)}
                                                        >
                                                            <i className="ri-quill-pen-fill"></i> E-signature
                                                        </button>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                        <div class="form-group col-md-6">
                                            <small class="form-text text-muted">Department</small>
                                            <input type="text" class="form-control-plaintext" value={userDetails?.onboardEmployeeDetails?.Department} readonly />
                                        </div>
                                        <div class="form-group col-md-6">
                                            <small class="form-text text-muted">Designation</small>
                                            <input type="text" class="form-control-plaintext" value={userDetails?.onboardEmployeeDetails?.Designation} readonly />
                                        </div>
                                        <div class="form-group col-md-6">
                                            <small class="form-text text-muted">Date of Joining</small>
                                            <input type="text" class="form-control-plaintext" value={moment(userDetails?.onboardEmployeeDetails?.DateOfJoining).format("DD-MM-YYYY")} readonly />
                                        </div>
                                        <div class="form-group col-md-6">
                                            <small class="form-text text-muted">Date of Leaving</small>
                                            <input type="text" class="form-control-plaintext" value={moment(userDetails?.onboardEmployeeDetails?.DateOfLeaving).format("DD-MM-YYYY")} readonly />
                                        </div>
                                        <div class="form-group col-md-6">
                                            <small class="form-text text-muted">Gender</small>
                                            <input type="text" class="form-control-plaintext" value={userDetails?.onboardEmployeeDetails?.Gender} readonly />
                                        </div>
                                        <div class="form-group col-md-6">
                                            <small class="form-text text-muted">Personal Email</small>
                                            <input type="text" class="form-control-plaintext" value={userDetails?.onboardEmployeeDetails?.PersonalEmail} readonly />
                                        </div>
                                        <div class="form-group col-md-6">
                                            <small class="form-text text-muted">Date of Birth</small>
                                            <input type="text" class="form-control-plaintext" value={moment(userDetails?.onboardEmployeeDetails?.DateOfBirth).format("DD-MM-YYYY")} readonly />
                                        </div>
                                        <div class="form-group col-md-6">
                                            <small class="form-text text-muted">Blood Group</small>
                                            <input type="text" class="form-control-plaintext" value={userDetails?.onboardEmployeeDetails?.BloodGroup} readonly />
                                        </div>
                                        <div class="form-group col-md-6">
                                            <small class="form-text text-muted">National ID</small>
                                            <input type="text" class="form-control-plaintext" value={userDetails?.onboardEmployeeDetails?.NID} readonly />
                                        </div>
                                        <div class="form-group col-md-6">
                                            <small class="form-text text-muted">Religion</small>
                                            <input type="text" class="form-control-plaintext" value={userDetails?.onboardEmployeeDetails?.Religion} readonly />
                                        </div>
                                        <div class="form-group col-md-6">
                                            <small class="form-text text-muted">Tribe</small>
                                            <input type="text" class="form-control-plaintext" value={userDetails?.onboardEmployeeDetails?.Tribe} readonly />
                                        </div>
                                        <div class="form-group col-md-6">
                                            <small class="form-text text-muted">Birth Place</small>
                                            <input type="text" class="form-control-plaintext" value={userDetails?.onboardEmployeeDetails?.BirthPlace} readonly />
                                        </div>
                                        <div class="form-group col-md-6">
                                            <small class="form-text text-muted">Marital Status</small>
                                            <input type="text" class="form-control-plaintext" value={userDetails?.onboardEmployeeDetails?.MaritalStatus} readonly />
                                        </div>
                                        <div class="form-group col-md-6">
                                            <small class="form-text text-muted">Citizenship</small>
                                            <input type="text" class="form-control-plaintext" value={userDetails?.onboardEmployeeDetails?.Citizenship} readonly />
                                        </div>
                                        <div class="form-group col-md-6">
                                            <small class="form-text text-muted">NSSF</small>
                                            <input type="text" class="form-control-plaintext" value={userDetails?.onboardEmployeeDetails?.NSSF} readonly />
                                        </div>
                                        <div class="form-group col-md-6">
                                            <small class="form-text text-muted">TIN</small>
                                            <input type="text" class="form-control-plaintext" value={userDetails?.onboardEmployeeDetails?.Tin} readonly />
                                        </div>
                                    </div>
                                    <hr />
                                    <h5 class="title accordion-title collapsed" data-toggle="collapse" data-target="#addressDetails"><i class="ri-information-line" title="Fill all required fields"></i> Address</h5>
                                    <div className="row collapse" id="addressDetails" data-parent="#ORGEmployeeDetails">
                                        <div className="col-lg-6 mt-4">
                                            <h6 className="mt-2">Present Address</h6>
                                            <div className="row">
                                                <div className="form-group col-md-12">
                                                    <label>Address Line 1</label>
                                                    <input type="text" class="form-control-plaintext" value={
                                                        userDetails.onboardEmployeeDetails
                                                            ?.AddressDetailsList?.CurrentAddress[0]
                                                            ?.addressOne
                                                    } onChange={(e) => setFormData({ ...formData, address: { ...formData.address, present: { ...formData.address.present, addressLine1: e.target.value } } })} />
                                                </div>
                                                <div className="form-group col-md-12">
                                                    <label>Address Line 2</label>
                                                    <input type="text" class="form-control-plaintext" value={
                                                        userDetails.onboardEmployeeDetails
                                                            ?.AddressDetailsList?.CurrentAddress[0]
                                                            ?.addressTwo
                                                    } onChange={(e) => setFormData({ ...formData, address: { ...formData.address, present: { ...formData.address.present, addressLine2: e.target.value } } })} />
                                                </div>
                                                <div className="form-group col-md-12">
                                                    <label>Town/City</label>
                                                    <input type="text" class="form-control-plaintext" value={
                                                        userDetails.onboardEmployeeDetails
                                                            ?.AddressDetailsList?.CurrentAddress[0]
                                                            ?.townCity
                                                    } onChange={(e) => setFormData({ ...formData, address: { ...formData.address, present: { ...formData.address.present, city: e.target.value } } })} />
                                                </div>
                                                <div className="form-group col-md-12">
                                                    <label>State</label>
                                                    <input type="text" class="form-control-plaintext" value={
                                                        userDetails.onboardEmployeeDetails
                                                            ?.AddressDetailsList?.CurrentAddress[0]
                                                            ?.state
                                                    } onChange={(e) => setFormData({ ...formData, address: { ...formData.address, present: { ...formData.address.present, state: e.target.value } } })} />
                                                </div>
                                                <div className="form-group col-md-6">
                                                    <label>Post Code</label>
                                                    <input type="text" class="form-control-plaintext" value={
                                                        userDetails.onboardEmployeeDetails
                                                            ?.AddressDetailsList?.CurrentAddress[0]
                                                            ?.postCode
                                                    } onChange={(e) => setFormData({ ...formData, address: { ...formData.address, present: { ...formData.address.present, postCode: e.target.value } } })} />
                                                </div>
                                                <div className="form-group col-md-6">
                                                    <label>Country Code</label>
                                                    <input type="text" class="form-control-plaintext" value={
                                                        userDetails.onboardEmployeeDetails
                                                            ?.AddressDetailsList?.CurrentAddress[0]
                                                            ?.countryCode
                                                    } onChange={(e) => setFormData({ ...formData, address: { ...formData.address, present: { ...formData.address.present, countryCode: e.target.value } } })} />
                                                </div>
                                            </div>
                                        </div>
                                        <div className="col-lg-6">
                                            <div className="custom-control custom-checkbox">
                                            </div>
                                            <h6 className="mt-2">Permanent Address</h6>
                                            <div className="row">
                                                <div className="form-group col-md-12">
                                                    <label>Address Line 1</label>
                                                    <input type="text" class="form-control-plaintext" value={
                                                        userDetails.onboardEmployeeDetails
                                                            ?.AddressDetailsList?.PermanentAddress[0]
                                                            ?.addressOne
                                                    } onChange={(e) => setFormData({ ...formData, address: { ...formData.address, permanent: { ...formData.address.permanent, addressLine1: e.target.value } } })} />
                                                </div>
                                                <div className="form-group col-md-12">
                                                    <label>Address Line 2</label>
                                                    <input type="text" class="form-control-plaintext" value={
                                                        userDetails.onboardEmployeeDetails
                                                            ?.AddressDetailsList?.PermanentAddress[0]
                                                            ?.addressTwo
                                                    } onChange={(e) => setFormData({ ...formData, address: { ...formData.address, permanent: { ...formData.address.permanent, addressLine2: e.target.value } } })} />
                                                </div>
                                                <div className="form-group col-md-12">
                                                    <label>Town/City</label>
                                                    <input type="text" class="form-control-plaintext" value={
                                                        userDetails.onboardEmployeeDetails
                                                            ?.AddressDetailsList?.PermanentAddress[0]
                                                            ?.townCity
                                                    } onChange={(e) => setFormData({ ...formData, address: { ...formData.address, permanent: { ...formData.address.permanent, city: e.target.value } } })} />
                                                </div>
                                                <div className="form-group col-md-12">
                                                    <label>State</label>
                                                    <input type="text" class="form-control-plaintext" value={
                                                        userDetails.onboardEmployeeDetails
                                                            ?.AddressDetailsList?.PermanentAddress[0]
                                                            ?.state
                                                    } onChange={(e) => setFormData({ ...formData, address: { ...formData.address, permanent: { ...formData.address.permanent, state: e.target.value } } })} />
                                                </div>
                                                <div className="form-group col-md-6">
                                                    <label>Post Code</label>
                                                    <input type="text" class="form-control-plaintext" value={
                                                        userDetails.onboardEmployeeDetails
                                                            ?.AddressDetailsList?.PermanentAddress[0]
                                                            ?.postCode
                                                    } onChange={(e) => setFormData({ ...formData, address: { ...formData.address, permanent: { ...formData.address.permanent, postCode: e.target.value } } })} />
                                                </div>
                                                <div className="form-group col-md-6">
                                                    <label>Country Code</label>
                                                    <input type="text" class="form-control-plaintext" value={
                                                        userDetails.onboardEmployeeDetails
                                                            ?.AddressDetailsList?.PermanentAddress[0]
                                                            ?.countryCode
                                                    } onChange={(e) => setFormData({ ...formData, address: { ...formData.address, permanent: { ...formData.address.permanent, countryCode: e.target.value } } })} />
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <hr />
                                    {/* Bank Details */}
                                    <h5 className="title accordion-title" data-toggle="collapse" data-target="#bankDetails">
                                        <i className="ri-information-line" title="Fill all required fields"></i> Bank
                                    </h5>
                                    <div className="row collapse show" id="bankDetails" data-parent="#ORGEmployeeDetails">
                                        <div className="form-group col-md-6">
                                            <label>Bank Name</label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                value={formData.bankDetails.bankName !== undefined && formData.bankDetails.bankName !== ""
                                                    ? formData.bankDetails.bankName
                                                    : userDetails?.personalInformation?.personalDetails?.bank?.bankName || ""}
                                                onChange={(e) => setFormData({ ...formData, bankDetails: { ...formData.bankDetails, bankName: e.target.value } })}
                                            />
                                        </div>
                                        <div className="form-group col-md-6">
                                            <label>Branch Name</label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                value={formData.bankDetails.branchName !== undefined && formData.bankDetails.branchName !== ""
                                                    ? formData.bankDetails.branchName
                                                    : userDetails?.personalInformation?.personalDetails?.bank?.branchName || ""}
                                                onChange={(e) => setFormData({ ...formData, bankDetails: { ...formData.bankDetails, branchName: e.target.value } })}
                                            />
                                        </div>
                                        <div className="form-group col-md-6">
                                            <label>Branch Code</label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                value={formData.bankDetails.branchCode !== undefined && formData.bankDetails.branchCode !== ""
                                                    ? formData.bankDetails.branchCode
                                                    : userDetails?.personalInformation?.personalDetails?.bank?.branchCode || ""}
                                                onChange={(e) => setFormData({ ...formData, bankDetails: { ...formData.bankDetails, branchCode: e.target.value } })}
                                            />
                                        </div>
                                        <div className="form-group col-md-6">
                                            <label>Account Number</label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                value={formData.bankDetails.accountNumber !== undefined && formData.bankDetails.accountNumber !== ""
                                                    ? formData.bankDetails.accountNumber
                                                    : userDetails?.personalInformation?.personalDetails?.bank?.accountNumber || ""}
                                                onChange={(e) => setFormData({ ...formData, bankDetails: { ...formData.bankDetails, accountNumber: e.target.value } })}
                                            />
                                        </div>
                                        <div className="form-group col-md-6">
                                            <label>Account Type</label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                value={formData.bankDetails.accountType !== undefined && formData.bankDetails.accountType !== ""
                                                    ? formData.bankDetails.accountType
                                                    : userDetails?.personalInformation?.personalDetails?.bank?.accountType || ""}
                                                onChange={(e) => setFormData({ ...formData, bankDetails: { ...formData.bankDetails, accountType: e.target.value } })}
                                            />
                                        </div>
                                        <div className="form-group col-md-6">
                                            <label>SWIFT/BIC Code</label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                value={formData.bankDetails.swiftCode !== undefined && formData.bankDetails.swiftCode !== ""
                                                    ? formData.bankDetails.swiftCode
                                                    : userDetails?.personalInformation?.personalDetails?.bank?.swiftCode || ""}
                                                onChange={(e) => setFormData({ ...formData, bankDetails: { ...formData.bankDetails, swiftCode: e.target.value } })}
                                            />
                                        </div>
                                    </div>
                                    <hr />
                                    {/* Family Details */}
                                    <h5 className="title accordion-title collapsed" data-toggle="collapse" data-target="#familyDetails">
                                        <i className="ri-information-line" title="Fill all required fields"></i> Family
                                    </h5>
                                    <div className="row collapse" id="familyDetails" data-parent="#ORGEmployeeDetails">
                                        <div className="form-group col-md-6">
                                            <label>Father Name</label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                value={formData.familyDetails.fatherName !== undefined && formData.familyDetails.fatherName !== ""
                                                    ? formData.familyDetails.fatherName
                                                    : userDetails?.personalInformation?.personalDetails?.family?.fatherName || ""}
                                                onChange={(e) => setFormData({ ...formData, familyDetails: { ...formData.familyDetails, fatherName: e.target.value } })}
                                            />
                                        </div>
                                        <div className="form-group col-md-6">
                                            <label>Mother Name</label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                value={formData.familyDetails.motherName !== undefined && formData.familyDetails.motherName !== ""
                                                    ? formData.familyDetails.motherName
                                                    : userDetails?.personalInformation?.personalDetails?.family?.motherName || ""}
                                                onChange={(e) => setFormData({ ...formData, familyDetails: { ...formData.familyDetails, motherName: e.target.value } })}
                                            />
                                        </div>
                                        <div className="form-group col-md-6">
                                            <label>Birth Certificate</label>
                                            <div className="input-group">
                                                <div className="custom-file">
                                                    <input
                                                        type="file"
                                                        className="custom-file-input"
                                                        id="birthCertificate"
                                                        onChange={handleFileUpload}
                                                    />
                                                    <label className="custom-file-label" htmlFor="birthCertificate">
                                                        {formData.familyDetails.birthCertificate ? formData.familyDetails.birthCertificate.name : 'Choose file'}
                                                    </label>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="form-group col-md-6">
                                            <label>Spouse Name</label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                value={formData.familyDetails.spouseName !== undefined && formData.familyDetails.spouseName !== ""
                                                    ? formData.familyDetails.spouseName
                                                    : userDetails?.personalInformation?.personalDetails?.family?.spouseName || ""}
                                                onChange={(e) => setFormData({ ...formData, familyDetails: { ...formData.familyDetails, spouseName: e.target.value } })}
                                            />
                                        </div>
                                        <div className="form-group col-md-6">
                                            <label>Marriage Certificate</label>
                                            <div className="input-group">
                                                <div className="custom-file">
                                                    <input
                                                        type="file"
                                                        className="custom-file-input"
                                                        id="marriageCertificate"
                                                        onChange={handleFileUpload}
                                                    />
                                                    <label className="custom-file-label" htmlFor="marriageCertificate">
                                                        {formData.familyDetails.marriageCertificate ? formData.familyDetails.marriageCertificate.name : 'Choose file'}
                                                    </label>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="form-group col-md-6">
                                            <label htmlFor="children">Children</label>
                                            <Select
                                                className="react-select-container"
                                                classNamePrefix="react-select"
                                                id="children"
                                                value={options.find(option => option.value === hasChildren) || null}
                                                onChange={handleDropdownChange}
                                                options={options}
                                                menuPortalTarget={document.body}
                                            />
                                        </div>
                                        {hasChildren === "Yes" &&
                                            childrenDetails.map((child, index) => (
                                                <>
                                                    <div key={index} className="form-group col-md-6">
                                                        <label htmlFor={`childName-${index}`}>
                                                            Children Name
                                                        </label>
                                                        <input
                                                            type="text"
                                                            className="form-control"
                                                            id={`childName-${index}`}
                                                            name="ChildrenName"
                                                            value={child.ChildrenName}
                                                            onChange={(e) => handleChildrenChange(index, e)}
                                                        />
                                                    </div>
                                                    <div key={index} className="form-group col-md-4">
                                                        <label htmlFor={`childAge-${index}`}>
                                                            Children Age
                                                        </label>
                                                        <input
                                                            type="number"
                                                            className="form-control"
                                                            id={`childName-${index}`}
                                                            name="Age"
                                                            min="0"
                                                            onKeyPress={handleKeyPress}
                                                            value={child.Age}
                                                            onChange={(e) => handleChildrenChange(index, e)}
                                                        />
                                                    </div>
                                                    {index > 0 && (
                                                        <div className="form-group col-md-2 d-flex align-items-end">
                                                            <button
                                                                className="btn btn-danger"
                                                                title="Remove Child"
                                                                type="button"
                                                                onClick={() => removeChild(index)}
                                                            >
                                                                <i className="ri-delete-bin-line"></i>
                                                            </button>
                                                        </div>
                                                    )}
                                                    {index === 0 && (
                                                        <div className="form-group col-md-2 d-flex align-items-end">
                                                            <button
                                                                className="btn btn-secondary"
                                                                title="Add Child"
                                                                type="button"
                                                                onClick={addChild}
                                                            >
                                                                <i className="ri-add-circle-line"></i>
                                                            </button>
                                                        </div>
                                                    )}
                                                </>
                                            ))}
                                    </div>
                                    <hr />
                                    <h5 class="title accordion-title collapsed" data-toggle="collapse" data-target="#educationDetails"><i class="ri-information-line" title="Fill all required fields"></i> Education </h5>
                                    <div className="row collapse" id="educationDetails" data-parent="#ORGEmployeeDetails">
                                        <div className="form-group col-md-6">
                                            <label>Institution Name</label>
                                            <input type="text" className="form-control"
                                                value={formData.education.institutionName !== undefined && formData.education.institutionName !== ""
                                                    ? formData.education.institutionName
                                                    : userDetails?.personalInformation?.personalDetails?.education?.institutionName || ""}
                                                onChange={(e) => setFormData({ ...formData, education: { ...formData.education, institutionName: e.target.value } })} />
                                        </div>
                                        <div className="form-group col-md-6">
                                            <label>Institution Location</label>
                                            <div className="input-group">
                                                <input type="text" className="form-control"
                                                    value={formData.education.city !== undefined && formData.education.city !== ""
                                                        ? formData.education.city
                                                        : userDetails?.personalInformation?.personalDetails?.education?.institutionCode || ""}
                                                    onChange={(e) => setFormData({ ...formData, education: { ...formData.education, institutionLocation: { ...formData.education.institutionLocation, city: e.target.value } } })} />
                                            </div>
                                        </div>
                                        <div className="form-group col-md-6">
                                            <label>Institution Type</label>
                                            <input type="text" className="form-control"
                                                value={formData.education.institutionType !== undefined && formData.education.institutionType !== ""
                                                    ? formData.education.institutionType
                                                    : userDetails?.personalInformation?.personalDetails?.education?.institutionType || ""}
                                                onChange={(e) => setFormData({ ...formData, education: { ...formData.education, institutionType: e.target.value } })} />
                                        </div>
                                        <div className="form-group col-md-6">
                                            <label>Degree/Qualification</label>
                                            <input type="text" className="form-control"
                                                value={formData.education.degree !== undefined && formData.education.degree !== ""
                                                    ? formData.education.degree
                                                    : userDetails?.personalInformation?.personalDetails?.education?.qualification || ""}
                                                onChange={(e) => setFormData({ ...formData, education: { ...formData.education, degree: e.target.value } })} />
                                        </div>
                                        <div className="form-group col-md-6">
                                            <label>Graduation Date</label>
                                            <input type="date" className="form-control"
                                                value={formData.education.graduationDate !== undefined && formData.education.graduationDate !== ""
                                                    ? formData.education.graduationDate
                                                    : moment(userDetails?.personalInformation?.personalDetails?.education?.graduationDate).format("YYYY-MM-DD") || ""}
                                                onChange={(e) => setFormData({ ...formData, education: { ...formData.education, graduationDate: e.target.value } })} />
                                        </div>
                                        <div className="form-group col-md-6">
                                            <label>Grade/CGPA</label>
                                            <input type="text" className="form-control"
                                                value={formData.education.grade !== undefined && formData.education.grade !== ""
                                                    ? formData.education.grade
                                                    : userDetails?.personalInformation?.personalDetails?.education?.grade || ""}
                                                onChange={(e) => setFormData({ ...formData, education: { ...formData.education, grade: e.target.value } })} />
                                        </div>
                                    </div>

                                    <hr />

                                    <h5 className="title accordion-title collapsed" data-toggle="collapse" data-target="#workExperienceDetails">
                                        <i className="ri-information-line" title="Fill all required fields"></i> Experience
                                    </h5>
                                    <div className="row collapse" id="workExperienceDetails" data-parent="#ORGEmployeeDetails">
                                        <div className="col-12">
                                            <div className="row">
                                                <div className="form-group col-md-6">
                                                    <label>Total Experience</label>
                                                    <div className="input-group">
                                                        <input
                                                            type="text"
                                                            className="form-control"
                                                            name="totalExperience"
                                                            value={formData.experience.totalExperience !== undefined && formData.experience.totalExperience !== ""
                                                                ? formData.experience.totalExperience
                                                                : userDetails?.personalInformation?.personalDetails?.experience?.totalExperience || ""}
                                                            onChange={(e) => setFormData({
                                                                ...formData,
                                                                experience: { ...formData.experience, totalExperience: e.target.value }
                                                            })}
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                            <h6 className="mt-2">Previous Employer Details</h6>
                                            <div className="row">
                                                <div className="form-group col-md-6">
                                                    <label>Employer Name</label>
                                                    <input
                                                        type="text"
                                                        className="form-control"
                                                        name="employerName"
                                                        value={formData.experience.employerName !== undefined && formData.experience.employerName !== ""
                                                            ? formData.experience.employerName
                                                            : userDetails?.personalInformation?.personalDetails?.experience?.employerName || ""}
                                                        onChange={(e) => setFormData({
                                                            ...formData,
                                                            experience: { ...formData.experience, employerName: e.target.value }
                                                        })}
                                                    />
                                                </div>
                                                <div className="form-group col-md-6">
                                                    <label>Designation</label>
                                                    <input
                                                        type="text"
                                                        className="form-control"
                                                        name="designation"
                                                        value={formData.experience.designation !== undefined && formData.experience.designation !== ""
                                                            ? formData.experience.designation
                                                            : userDetails?.personalInformation?.personalDetails?.experience?.designation || ""}
                                                        onChange={(e) => setFormData({
                                                            ...formData,
                                                            experience: { ...formData.experience, designation: e.target.value }
                                                        })}
                                                    />
                                                </div>
                                                <div className="form-group col-md-6">
                                                    <label>Department</label>
                                                    <input
                                                        type="text"
                                                        className="form-control"
                                                        name="department"
                                                        value={formData.experience.department !== undefined && formData.experience.department !== ""
                                                            ? formData.experience.department
                                                            : userDetails?.personalInformation?.personalDetails?.experience?.department || ""}
                                                        onChange={(e) => setFormData({
                                                            ...formData,
                                                            experience: { ...formData.experience, department: e.target.value }
                                                        })}
                                                    />
                                                </div>
                                                <div className="form-group col-md-6">
                                                    <label>Start Date</label>
                                                    <div className="input-group">
                                                        <input
                                                            type="date"
                                                            className="form-control"
                                                            name="startDate"
                                                            value={formData.experience.startDate !== undefined && formData.experience.startDate !== ""
                                                                ? formData.experience.startDate
                                                                : moment(userDetails?.personalInformation?.personalDetails?.experience?.startDate).format("YYYY-MM-DD") || ""}
                                                            onChange={(e) => setFormData({
                                                                ...formData,
                                                                experience: { ...formData.experience, startDate: e.target.value }

                                                            })}
                                                        />
                                                    </div>
                                                </div>
                                                <div className="form-group col-md-6">
                                                    <label>End Date</label>
                                                    <div className="input-group">
                                                        <input
                                                            type="date"
                                                            className="form-control"
                                                            name="endDate"
                                                            value={formData.experience.endDate !== undefined && formData.experience.endDate !== ""
                                                                ? formData.experience.endDate
                                                                : moment(userDetails?.personalInformation?.personalDetails?.experience?.endDate).format("YYYY-MM-DD") || ""}
                                                            onChange={(e) => setFormData({
                                                                ...formData,
                                                                experience: { ...formData.experience, endDate: e.target.value }
                                                            })}
                                                        />
                                                    </div>
                                                </div>
                                                <div className="form-group col-md-6">
                                                    <label>Manager Name</label>
                                                    <input
                                                        type="text"
                                                        className="form-control"
                                                        name="managerName"
                                                        value={formData.experience.managerName !== undefined && formData.experience.managerName !== ""
                                                            ? formData.experience.managerName
                                                            : userDetails?.personalInformation?.personalDetails?.experience?.managerName || ""}
                                                        onChange={(e) => setFormData({
                                                            ...formData,
                                                            experience: { ...formData.experience, managerName: e.target.value }
                                                        })}
                                                    />
                                                </div>
                                                <div className="form-group col-md-6">
                                                    <label>Manager Email</label>
                                                    <input
                                                        type="email"
                                                        className="form-control"
                                                        name="managerEmail"
                                                        value={formData.experience.managerEmail !== undefined && formData.experience.managerEmail !== ""
                                                            ? formData.experience.managerEmail
                                                            : userDetails?.personalInformation?.personalDetails?.experience?.managerEmail || ""}
                                                        onChange={(e) => setFormData({
                                                            ...formData,
                                                            experience: { ...formData.experience, managerEmail: e.target.value }
                                                        })}
                                                    />
                                                </div>
                                                <div className="form-group col-md-6">
                                                    <label>Location City</label>
                                                    <input
                                                        type="text"
                                                        className="form-control"
                                                        name="locationCity"
                                                        value={formData.experience.locationCity !== undefined && formData.experience.locationCity !== ""
                                                            ? formData.experience.locationCity
                                                            : userDetails?.personalInformation?.personalDetails?.experience?.location || ""}
                                                        onChange={(e) => setFormData({
                                                            ...formData,
                                                            experience: { ...formData.experience, locationCity: e.target.value }
                                                        })}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <hr />
                                    <h5 className="title accordion-title collapsed" data-toggle="collapse" data-target="#achievementsDetails">
                                        <i className="ri-information-line" title="Fill all required fields"></i> Achievements
                                    </h5>
                                    <div className="row collapse" id="achievementsDetails" data-parent="#ORGEmployeeDetails">
                                        <div className="col-12">
                                            {formData.achievements.map((achievement, index) => (
                                                <div className="row align-items-center" key={index}>
                                                    <div className="form-group col-md-6">
                                                        <label>Title</label>
                                                        <input
                                                            type="text"
                                                            className="form-control"
                                                            name={`title_${index}`}
                                                            value={achievement.Title}
                                                            onChange={(e) => {
                                                                const newAchievements = [...formData.achievements];
                                                                newAchievements[index].Title = e.target.value;
                                                                setFormData({ ...formData, achievements: newAchievements });
                                                            }}
                                                        />
                                                    </div>
                                                    <div className="form-group col-md-6">
                                                        <label>Achievement Date</label>
                                                        <input
                                                            type="date"
                                                            className="form-control"
                                                            name={`achievementDate_${index}`}
                                                            value={achievement.AchievementDate}
                                                            onChange={(e) => {
                                                                const newAchievements = [...formData.achievements];
                                                                newAchievements[index].AchievementDate = e.target.value;
                                                                setFormData({ ...formData, achievements: newAchievements });
                                                            }}
                                                        />
                                                    </div>
                                                    <div className="form-group col-md-12">
                                                        <label>Certificate</label>
                                                        <div className="input-group">
                                                            <div className="custom-file">
                                                                <input
                                                                    type="file"
                                                                    className="custom-file-input"
                                                                    name={`certificate_${index}`}
                                                                    onChange={(e) => {
                                                                        const newAchievements = [...formData.achievements];
                                                                        newAchievements[index].Certificate = e.target.files[0]; // Store the file
                                                                        setFormData({ ...formData, achievements: newAchievements });
                                                                    }}
                                                                    id={`certificate_${index}`}
                                                                />
                                                                <label className="custom-file-label" htmlFor={`certificate_${index}`}>
                                                                    {formData.achievements[index].Certificate ? formData.achievements[index].Certificate.name : 'Choose file'}
                                                                </label>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className="form-group col-md-12 pb-4 border-bottom">
                                                        <label>Comments</label>
                                                        <textarea
                                                            type="textarea"
                                                            rows="2"
                                                            className="form-control"
                                                            name={`comments_${index}`}
                                                            value={achievement.Comments}
                                                            onChange={(e) => {
                                                                const newAchievements = [...formData.achievements];
                                                                newAchievements[index].Comments = e.target.value;
                                                                setFormData({ ...formData, achievements: newAchievements });
                                                            }}
                                                        />
                                                    </div>
                                                    <div className="col-md-1 d-flex align-items-center">
                                                        <button
                                                            type="button"
                                                            className="btn btn-primary btn-sm mr-2"
                                                            onClick={() => {
                                                                setFormData({
                                                                    ...formData,
                                                                    achievements: [
                                                                        ...formData.achievements,
                                                                        { Title: '', AchievementDate: '', Comments: '' },
                                                                    ],
                                                                });
                                                            }}
                                                        >
                                                            <i className="ri-add-line"></i>
                                                        </button>
                                                        <button
                                                            type="button"
                                                            className="btn btn-danger btn-sm"
                                                            onClick={() => {
                                                                const newAchievements = formData.achievements.filter((_, i) => i !== index);
                                                                setFormData({ ...formData, achievements: newAchievements });
                                                            }}
                                                        >
                                                            <i className="ri-delete-bin-6-line"></i>
                                                        </button>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                    <hr />
                                    <h5 className="title accordion-title collapsed" data-toggle="collapse" data-target="#skills">
                                        <i className="ri-information-line" title="Fill all required fields"></i> Skills
                                    </h5>
                                    <div className="row collapse" id="skills" data-parent="#ORGEmployeeDetails">
                                        <div className="col-12">
                                            {formData.skills.map((skill, index) => (
                                                <div key={index} className="input-group mb-2">
                                                    <input
                                                        type="text"
                                                        className="form-control"
                                                        placeholder={`Skill ${index + 1}`}
                                                        value={skill}
                                                        onChange={(e) => {
                                                            const newSkills = [...formData.skills];
                                                            newSkills[index] = e.target.value;
                                                            setFormData({ ...formData, skills: newSkills });
                                                        }}
                                                    />
                                                    <div className="input-group-append">
                                                        <button
                                                            className="btn btn-danger"
                                                            type="button"
                                                            onClick={() => {
                                                                const newSkills = formData.skills.filter((_, i) => i !== index);
                                                                setFormData({ ...formData, skills: newSkills });
                                                            }}
                                                        >
                                                            <i className="ri-delete-bin-6-line"></i>
                                                        </button>
                                                    </div>
                                                </div>
                                            ))}
                                            <button
                                                type="button"
                                                className="btn btn-primary"
                                                onClick={() => setFormData({ ...formData, skills: [...formData.skills, ''] })}
                                            >
                                                <i className="fas fa-plus"></i> Add Skill
                                            </button>
                                        </div>
                                    </div>
                                    <hr />
                                    <h5 class="title accordion-title collapsed" data-toggle="collapse" data-target="#healthDetails"><i class="ri-information-line" title="Fill all required fields"></i> Health </h5>
                                    <div className="row collapse" id="healthDetails" data-parent="#ORGEmployeeDetails">
                                        <div className="form-group col-md-6">
                                            <label>Medical Conditions and Allergies</label>
                                            <Select
                                                value={options.find(option => option.value === formData.health.medicalConditionsAllergies)}
                                                onChange={handleMedicalChange}
                                                options={options}
                                                classNamePrefix="react-select"
                                            />
                                        </div>
                                        <div className="form-group col-md-6">
                                            <label>Known Chronic Conditions</label>
                                            <input type="text" className="form-control"
                                                value={formData.health.chronicConditions !== undefined && formData.health.chronicConditions !== ""
                                                    ? formData.health.chronicConditions
                                                    : userDetails?.personalInformation?.personalDetails?.health?.knownChronicConditions || ""}
                                                onChange={(e) => setFormData({ ...formData, health: { ...formData.health, chronicConditions: e.target.value } })} />
                                        </div>
                                        <div className="form-group col-md-6">
                                            <label>Known Chronic Allergies</label>
                                            <input type="text" className="form-control"
                                                value={formData.health.chronicAllergies !== undefined && formData.health.chronicAllergies !== ""
                                                    ? formData.health.chronicAllergies
                                                    : userDetails?.personalInformation?.personalDetails?.health?.knownChronicAllergies || ""}
                                                onChange={(e) => setFormData({ ...formData, health: { ...formData.health, chronicAllergies: e.target.value } })} />
                                        </div>
                                        <div className="form-group col-md-6">
                                            <label>Vaccination Status</label>
                                            <Select
                                                value={vaccinationOptions.find(option => option.value === formData.health.vaccinationStatus)}
                                                onChange={handleVaccinationChange}
                                                options={vaccinationOptions}
                                                classNamePrefix="react-select"
                                            />
                                        </div>
                                        <div className="form-group col-md-6">
                                            <label>Type of Vaccination</label>
                                            <input type="text" className="form-control"
                                                value={formData.health.vaccinationType !== undefined && formData.health.vaccinationType !== ""
                                                    ? formData.health.vaccinationType
                                                    : userDetails?.personalInformation?.personalDetails?.health?.vaccinationType || ""}
                                                onChange={(e) => setFormData({ ...formData, health: { ...formData.health, vaccinationType: e.target.value } })} />
                                        </div>
                                        <div className="form-group col-md-6">
                                            <label>Date of Last Vaccination</label>
                                            <input type="date" className="form-control"
                                                value={formData.health.lastVaccinationDate !== undefined && formData.health.lastVaccinationDate !== ""
                                                    ? formData.health.lastVaccinationDate
                                                    : moment(userDetails?.personalInformation?.personalDetails?.health?.dateOfLastVaccination).format("YYYY-MM-DD") || ""}
                                                onChange={(e) => setFormData({ ...formData, health: { ...formData.health, lastVaccinationDate: e.target.value } })} />
                                        </div>
                                        <div className="form-group col-md-6">
                                            <label>Work-Related Injuries and Illnesses</label>
                                            <Select
                                                value={
                                                    formData.health.workRelatedInjuries !== undefined && formData.health.workRelatedInjuries !== ""
                                                        ? workInjuryOptions.find(option => option.value === formData.health.workRelatedInjuries)
                                                        : workInjuryOptions.find(option => option.value === userDetails?.personalInformation?.personalDetails?.health?.workRelatedInjuries) || ""}
                                                onChange={handleWorkInjuryChange}
                                                options={workInjuryOptions}
                                                classNamePrefix="react-select"
                                            />
                                        </div>
                                        <div className="form-group col-md-6">
                                            <label>Date of Incident</label>
                                            <input type="date" className="form-control"
                                                value={formData.health.incidentDate !== undefined && formData.health.incidentDate !== ""
                                                    ? formData.health.incidentDate
                                                    : moment(userDetails?.personalInformation?.personalDetails?.health?.dateOfIncident).format("YYYY-MM-DD") || ""}
                                                onChange={(e) => setFormData({ ...formData, health: { ...formData.health, incidentDate: e.target.value } })} />
                                        </div>
                                        <div className="form-group col-md-12 pb-4 border-bottom">
                                            <label>Incident Description</label>
                                            <textarea rows="2" className="form-control"
                                                // value={formData.health.incidentDescription}
                                                value={formData.health.incidentDescription !== undefined && formData.health.incidentDescription !== ""
                                                    ? formData.health.incidentDescription
                                                    : userDetails?.personalInformation?.personalDetails?.health?.incidentDescription || ""}
                                                onChange={(e) => setFormData({ ...formData, health: { ...formData.health, incidentDescription: e.target.value } })}></textarea>
                                        </div>
                                        <div className="form-group col-md-6">
                                            <label>Do you have personal doctor</label>
                                            <Select
                                                value={personalDetailsOptions.find(option => option.value === formData.health.hasPersonalDetails)}
                                                onChange={handlePersonalDetailsChange}
                                                options={personalDetailsOptions}
                                                classNamePrefix="react-select"
                                            />
                                        </div>
                                        {formData?.health?.hasPersonalDetails === 'Yes' ?
                                            <>
                                                <div className="form-group col-md-6">
                                                    <label>Doctor Name</label>
                                                    <input type="text" className="form-control"
                                                        value={formData.health.doctorName !== undefined && formData.health.doctorName !== ""
                                                            ? formData.health.doctorName
                                                            : userDetails?.personalInformation?.personalDetails?.health?.doctorName || ""}
                                                        onChange={(e) => setFormData({ ...formData, health: { ...formData.health, doctorName: e.target.value } })} />
                                                </div>
                                                <div className="form-group col-md-6">
                                                    <label>Doctor Contact</label>
                                                    <input type="text" className="form-control"
                                                        value={formData.health.doctorContact !== undefined && formData.health.doctorContact !== ""
                                                            ? formData.health.doctorContact
                                                            : userDetails?.personalInformation?.personalDetails?.health?.doctorContact || ""}
                                                        onChange={(e) => setFormData({ ...formData, health: { ...formData.health, doctorContact: e.target.value } })} onKeyPress={handleKeyPress} />
                                                </div>
                                                <div className="form-group col-md-6">
                                                    <label>Doctor Email</label>
                                                    <input type="email" className="form-control"
                                                        value={formData.health.doctorEmail !== undefined && formData.health.doctorEmail !== ""
                                                            ? formData.health.doctorEmail
                                                            : userDetails?.personalInformation?.personalDetails?.health?.doctorEmail || ""}
                                                        onChange={(e) => setFormData({ ...formData, health: { ...formData.health, doctorEmail: e.target.value } })} />
                                                </div>
                                                <div className="form-group col-md-6">
                                                    <label>Hospital Name</label>
                                                    <input type="text" className="form-control"
                                                        value={formData.health.hospitalName !== undefined && formData.health.hospitalName !== ""
                                                            ? formData.health.hospitalName
                                                            : userDetails?.personalInformation?.personalDetails?.health?.hospitalName || ""}
                                                        onChange={(e) => setFormData({ ...formData, health: { ...formData.health, hospitalName: e.target.value } })} />
                                                </div>
                                                <div className="form-group col-md-6">
                                                    <label>Health Certificate</label>
                                                    <div className="input-group">
                                                        <div className="custom-file">
                                                            <input type="file" className="custom-file-input" id="healthCertificate" onChange={handleFileUpload} />
                                                            <label className="custom-file-label" htmlFor="healthCertificate">{formData.health.healthCertificate ? formData.health.healthCertificate.name : 'Choose file'}</label>
                                                        </div>
                                                    </div>
                                                </div>
                                            </>
                                            : ""}
                                    </div>

                                    <hr />
                                    <h5 class="title accordion-title collapsed" data-toggle="collapse" data-target="#addtionalDetails"><i class="ri-information-line" title="Fill all required fields"></i> Additional Information</h5>
                                    <div className="row collapse" id="addtionalDetails" data-parent="#ORGEmployeeDetails">
                                        <div className="form-group col-md-6">
                                            <label>KIN</label>
                                            <input type="text" className="form-control"
                                                value={formData.additionalInformation?.kin !== undefined && formData.additionalInformation?.kin !== ""
                                                    ? formData.additionalInformation?.kin
                                                    : userDetails?.personalInformation?.personalDetails?.additionalInformation?.kin || ""}
                                                onChange={(e) => setFormData({ ...formData, additional: { ...formData.additional, kin: e.target.value } })} />
                                        </div>
                                        <div className="form-group col-md-6">
                                            <label>Passport</label>
                                            <input type="text" className="form-control"
                                                value={formData.additionalInformation?.passport !== undefined && formData.additionalInformation?.passport !== ""
                                                    ? formData.additionalInformation?.passport
                                                    : userDetails?.personalInformation?.personalDetails?.additionalInformation?.passport || ""}
                                                onChange={(e) => setFormData({ ...formData, additional: { ...formData.additional, passport: e.target.value } })} />
                                        </div>
                                        <div className="form-group col-md-6">
                                            <label>Passport Copy</label>
                                            <div className="input-group">
                                                <div className="custom-file">
                                                    <input type="file" className="custom-file-input" id="passportCopy" onChange={handleFileUpload} />
                                                    <label className="custom-file-label" htmlFor="passportCopy">{formData.additional.passportCopy ? formData.additional.passportCopy.name : 'Choose file'}</label>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="form-group col-md-6">
                                            <label>Conduct Certificate</label>
                                            <div className="input-group">
                                                <div className="custom-file">
                                                    <input type="file" className="custom-file-input" id="conductCertificate" onChange={handleFileUpload} />
                                                    <label className="custom-file-label" htmlFor="conductCertificate">{formData.additional.conductCertificate ? formData.additional.conductCertificate.name : 'Choose file'}</label>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="form-group col-md-6">
                                            <label>Referees Details/Contact</label>
                                            <input type="text" className="form-control"
                                                value={formData.additionalInformation?.referees !== undefined && formData.additionalInformation?.referees !== ""
                                                    ? formData.additionalInformation?.referees
                                                    : userDetails?.personalInformation?.personalDetails?.additionalInformation?.referenceDetails || ""}
                                                onChange={(e) => setFormData({ ...formData, additional: { ...formData.additional, referees: e.target.value } })} />
                                        </div>
                                    </div>

                                    <hr />
                                    <h5 class="title accordion-title collapsed" data-toggle="collapse" data-target="#submittedDocumentsDetails"><i class="ri-information-line" title="Fill all required fields"></i>Documents</h5>
                                    <div className="row collapse" id="submittedDocumentsDetails" data-parent="#ORGEmployeeDetails">
                                        <div className="col-lg-12 col-md-12">
                                            <div className="form-group">
                                                <div className="d-flex align-items-center">
                                                    <div className="custom-file mr-2">
                                                        <input
                                                            type="file"
                                                            className="custom-file-input"
                                                            id="Document"
                                                            onChange={(e) => setFile(e.target.files[0])}
                                                        />
                                                        <label className="custom-file-label" htmlFor="Document">
                                                            {file ? file.name : "Choose a file"}
                                                        </label>
                                                    </div>
                                                    <button
                                                        type="button"
                                                        className="btn btn-secondary"
                                                        onClick={() => {
                                                            if (file) {
                                                                setUploadedFiles([...uploadedFiles, file]);
                                                                setFile(null); // Clear the file input
                                                            }
                                                        }}
                                                    >
                                                        Upload
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="col-lg-12 col-md-12">
                                            <div className="container-fluid my-2 px-0">
                                                <div className="row">
                                                    {uploadedFiles.map((file, index) => (
                                                        <div key={index} className="col-6 mb-2">
                                                            <button
                                                                className="btn btn-link text-danger btn-action"
                                                                type="button"
                                                                onClick={() => {
                                                                    const newFiles = uploadedFiles.filter((_, i) => i !== index);
                                                                    setUploadedFiles(newFiles);
                                                                }}
                                                            >
                                                                <i className="ri-delete-bin-6-line" />
                                                            </button>
                                                            <a
                                                                href="#"
                                                                onClick={() => window.open(URL.createObjectURL(file), "_blank")}
                                                            >
                                                                {file.name}
                                                            </a>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <hr />
                                    {/* Payment Information: Only for Consultants */}
                                    {userDetails?.onboardEmployeeDetails?.EmployeeType === "Consultant" && Array.isArray(userDetails?.onboardEmployeeDetails?.ConsultantDetails) && userDetails.onboardEmployeeDetails.ConsultantDetails.length > 0 && (
                                        <>
                                            <h5 className="title accordion-title collapsed" data-toggle="collapse" data-target="#paymentInformation"><i className="ri-information-line"></i>Payment Information</h5>
                                            <div className="row collapse" id="paymentInformation" data-parent="#ORGEmployeeDetails">
                                                <div className="form-group col-md-6">
                                                    <small className="form-text text-muted">Vendor</small>
                                                    <input type="text" className="form-control-plaintext" value={userDetails.onboardEmployeeDetails.ConsultantDetails[0].agencyName || ""} readOnly />
                                                </div>
                                                <div className="form-group col-md-6">
                                                    <small className="form-text text-muted">Payment Type</small>
                                                    <input type="text" className="form-control-plaintext" value={userDetails.onboardEmployeeDetails.ConsultantDetails[0].paymentType || ""} readOnly />
                                                </div>
                                                <div className="form-group col-md-6">
                                                    <small className="form-text text-muted">Currency</small>
                                                    <input type="text" className="form-control-plaintext" value={userDetails.onboardEmployeeDetails.ConsultantDetails[0].currency || ""} readOnly />
                                                </div>
                                                <div className="form-group col-md-6">
                                                    <small className="form-text text-muted">Price</small>
                                                    <input type="text" className="form-control-plaintext" value={userDetails.onboardEmployeeDetails.ConsultantDetails[0].price || ""} readOnly />
                                                </div>
                                                <div className="form-group col-md-6">
                                                    <small className="form-text text-muted">Tax%</small>
                                                    <input type="text" className="form-control-plaintext" value={userDetails.onboardEmployeeDetails.ConsultantDetails[0].tax || ""} readOnly />
                                                </div>
                                            </div>
                                        </>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div class="row fixed-footer">
                <div class="col-lg-12 text-center">
                    <button class="btn btn-light" type="reset">Cancel</button>
                    <button class="btn btn-primary" type="button" onClick={handleSubmit}>Submit <i class="ri-checkbox-circle-line"></i></button>
                </div>
            </div>
            {
                isModalOpen && (
                    <div className="modal fade show" tabIndex="-1" role="dialog" aria-labelledby="cancelLeaveConfirmModalLabel" aria-hidden="true" style={{ display: 'block' }}>
                        <div className="modal-dialog modal-sm modal-dialog-centered" role="document">
                            <div className="modal-content shadow">
                                <div className="modal-header py-2">
                                    <h6 className="modal-title" id="cancelLeaveConfirmModalLabel">Are you sure to submit it?</h6>
                                    <button className="close" type="button" onClick={() => setIsModalOpen(false)} aria-label="Close">
                                        <span aria-hidden="true"><i className="ri-close-circle-fill font-size-20"></i></span>
                                    </button>
                                </div>
                                <div className="modal-body">
                                    Select "<b style={{ color: '#25B740' }}>Yes</b>" below if you want to Proceed with the information. <br /> <br />
                                    <b style={{ color: 'red' }}>Note*</b>- For any further changes you need to contact Admin/HR
                                </div>
                                <div className="modal-footer py-2 justify-content-center">
                                    <button className="btn btn-light" type="button" onClick={() => setIsModalOpen(false)}>No</button>
                                    <button className="btn btn-primary" type="button" onClick={handleYes}>Yes</button>
                                </div>
                            </div>
                        </div>
                    </div>
                )
            }
        </div >
    )
}

export default PersonalInformation
